/**
 * Cross-device haptic helper.
 *
 * - Android (Chrome): navigator.vibrate() works out of the box.
 * - iOS (Safari): navigator.vibrate is not implemented on most iOS versions,
 *   but iOS 17.4+ Safari supports the "switch" hidden checkbox trick to fire
 *   a real haptic tick. We create a single hidden switch element once and
 *   toggle it to request feedback where the API allows.
 *
 * All calls are safe on desktop (silent no-op).
 */

let switchEl = null;

function ensureSwitch() {
  if (typeof document === 'undefined') return null;
  if (switchEl) return switchEl;
  switchEl = document.createElement('input');
  switchEl.type = 'checkbox';
  switchEl.setAttribute('switch', '');
  Object.assign(switchEl.style, {
    position: 'fixed',
    top: '-9999px',
    left: '-9999px',
    width: '1px',
    height: '1px',
    opacity: '0',
    pointerEvents: 'none',
  });
  switchEl.setAttribute('aria-hidden', 'true');
  document.body.appendChild(switchEl);
  return switchEl;
}

const PATTERNS = {
  tap:     10,           // default click
  light:   6,
  success: [8, 40, 12],  // check / save
  warning: [12, 20, 12, 20, 12], // shake / validation
  heavy:   35,
};

/** Fire a haptic tap. `kind` picks a pattern; default is 'tap'. */
export function haptic(kind = 'tap') {
  const pattern = PATTERNS[kind] ?? PATTERNS.tap;
  try {
    // Android + Chrome-ish support
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(pattern);
    }
  } catch { /* ignore */ }
  try {
    // iOS 17.4+ Safari haptics trick: toggle a hidden switch element.
    const el = ensureSwitch();
    if (el) {
      el.checked = !el.checked;
    }
  } catch { /* ignore */ }
}

/** Convenience React hook — returns the same fn but memoised. */
export function useHaptic() {
  return haptic;
}
